// FRF advisory panel for the Results area of a Study.
//
// The backend writes a per-run frf_summary artifact (3 bands x 4 corners of
// scalars: magnitude bias in dB, phase error in degrees, coherence 0..1). This
// panel renders it as tables rather than charts: the artifact carries no
// per-frequency spectrum, so a plotted curve would be invented data. Tables
// read the scalars honestly.
//
// Two modes, driven by the Results-area view:
//   single:  the one displayed COMPLETE run: a 3x3 band summary grid (with
//            coherence-aware de-emphasis), a collapsible per-corner detail
//            table, and the backend advisory note verbatim.
//   compare: the 2..4 runs selected for comparison: their mid-band magnitude
//             and phase per corner side by side, coloured by whether each run
//             is closer to 0 than the first (reference) run.
//
// Fetches are keyed on run id, abortable, and cached in a Map: frf_summary
// artifacts are immutable, so a run is fetched at most once.
import { useEffect, useRef, useState, type ReactNode } from "react";

import type { Run, Test } from "../api/tests";
import {
  fetchFrfSummary,
  FRF_BANDS,
  FRF_BAND_LABEL,
  FRF_CORNERS,
  FRF_CORNER_LABEL,
  type FrfArtifact,
  type FrfBandKey,
  type FrfCornerKey,
  type FrfSummary,
} from "../api/frf";

// Coherence below this floor de-emphasises a band: its magnitude/phase numbers
// are advisory (the recorded input carried little energy the sim could track).
const COHERENCE_FLOOR = 0.5;
const LOW_COHERENCE_TITLE = "low coherence, advisory";

export type FrfPanelProps = {
  test: Test;
  // Which Results-area view is active. "single" shows one run's summary;
  // "compare" shows the selected runs side by side.
  mode: "single" | "compare";
  // single mode: the displayed COMPLETE run (already resolved by the parent to
  // the pinned-or-most-recent-complete run), or null when no complete run
  // exists, in which case the panel renders nothing.
  singleRunId: number | null;
  // compare mode: the ordered (2..4) selected run ids, first = reference.
  compareRunIds: number[];
};

type FetchState =
  | { kind: "loading" }
  | { kind: "ready"; data: FrfSummary }
  | { kind: "error"; message: string };

// --- formatting -------------------------------------------------------------

// Missing (NaN-omitted) leaves render as a bare dash, the table missing-value
// marker used across the Results panes.
function fmt(v: number | null | undefined, digits: number): string {
  return typeof v === "number" && Number.isFinite(v) ? v.toFixed(digits) : "—";
}

function finite(v: number | null | undefined): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

// --- band summary grid (single mode) ----------------------------------------

type GridRow = {
  key: string;
  label: string;
  field: "mag_db_absmean" | "phase_deg_absmean" | "coherence_mean";
  digits: number;
};

const GRID_ROWS: GridRow[] = [
  { key: "mag", label: "mag (dB)", field: "mag_db_absmean", digits: 1 },
  { key: "phase", label: "phase (deg)", field: "phase_deg_absmean", digits: 1 },
  { key: "coherence", label: "coherence", field: "coherence_mean", digits: 2 },
];

function bandLowCoherence(art: FrfArtifact, band: FrfBandKey): boolean {
  const c = finite(art.bands?.[band]?.coherence_mean);
  return c !== null && c < COHERENCE_FLOOR;
}

function BandSummaryGrid({ art }: { art: FrfArtifact }) {
  const low: Record<FrfBandKey, boolean> = {
    lo: bandLowCoherence(art, "lo"),
    mid: bandLowCoherence(art, "mid"),
    hi: bandLowCoherence(art, "hi"),
  };
  return (
    <table className="frf-grid num" data-testid="frf-band-grid">
      <thead>
        <tr>
          <th className="frf-l" />
          {FRF_BANDS.map((b) => (
            <th
              key={b}
              className={low[b] ? "frf-muted" : undefined}
              title={low[b] ? LOW_COHERENCE_TITLE : undefined}
              data-testid={`frf-grid-head-${b}`}
            >
              {b}
              <span className="frf-band-range">{FRF_BAND_LABEL[b]}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {GRID_ROWS.map((row) => (
          <tr key={row.key} data-testid={`frf-grid-row-${row.key}`}>
            <th scope="row" className="frf-l">
              {row.label}
            </th>
            {FRF_BANDS.map((b) => (
              <td
                key={b}
                className={low[b] ? "frf-muted" : undefined}
                title={low[b] ? LOW_COHERENCE_TITLE : undefined}
                data-testid={`frf-grid-${row.key}-${b}`}
              >
                {fmt(art.bands?.[b]?.[row.field], row.digits)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --- per-corner detail table (single mode, collapsible) ---------------------

type CornerQuantity = {
  key: string;
  label: string;
  field: "mag_db" | "phase_deg" | "coherence";
  digits: number;
};

const CORNER_QUANTITIES: CornerQuantity[] = [
  { key: "mag", label: "mag (dB)", field: "mag_db", digits: 1 },
  { key: "phase", label: "phase (deg)", field: "phase_deg", digits: 1 },
  { key: "coherence", label: "coherence", field: "coherence", digits: 2 },
];

function PerCornerDetail({ art }: { art: FrfArtifact }) {
  return (
    <details className="frf-per-corner">
      <summary data-testid="frf-per-corner-summary">Per-corner detail</summary>
      <table className="frf-corner-table num" data-testid="frf-per-corner-table">
        <thead>
          <tr>
            <th className="frf-l">corner</th>
            <th className="frf-l">quantity</th>
            {FRF_BANDS.map((b) => (
              <th key={b}>{b}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {FRF_CORNERS.map((corner) =>
            CORNER_QUANTITIES.map((q, qi) => {
              const triplet = art.per_corner?.[corner]?.[q.field];
              return (
                <tr
                  key={`${corner}-${q.key}`}
                  data-testid={`frf-corner-${corner}-${q.key}`}
                >
                  {qi === 0 ? (
                    <th scope="rowgroup" rowSpan={3} className="frf-l">
                      {FRF_CORNER_LABEL[corner]}
                    </th>
                  ) : null}
                  <td className="frf-l">{q.label}</td>
                  {FRF_BANDS.map((b) => (
                    <td key={b}>{fmt(triplet?.[b], q.digits)}</td>
                  ))}
                </tr>
              );
            }),
          )}
        </tbody>
      </table>
    </details>
  );
}

// --- compare table (compare mode) -------------------------------------------

type CompareQuantity = {
  key: string;
  label: string;
  field: "mag_db" | "phase_deg";
  digits: number;
};

const COMPARE_QUANTITIES: CompareQuantity[] = [
  { key: "mag", label: "mag (dB)", field: "mag_db", digits: 1 },
  { key: "phase", label: "phase (deg)", field: "phase_deg", digits: 1 },
];

type CompareCol = {
  runId: number;
  runNumber: number | null;
  summary: FrfArtifact | null; // present only when available
  unavailableReason: string | null; // reason line when not available
};

// Green when this run's value is closer to 0 than the reference; amber when
// further. Neutral (no class) when either side is missing.
function closenessTone(
  value: number | null,
  reference: number | null,
): string | undefined {
  if (value === null || reference === null) return undefined;
  return Math.abs(value) < Math.abs(reference) ? "frf-better" : "frf-worse";
}

function cornerMid(
  summary: FrfArtifact | null,
  corner: FrfCornerKey,
  field: "mag_db" | "phase_deg",
): number | null {
  if (!summary) return null;
  return finite(summary.per_corner?.[corner]?.[field]?.mid);
}

function CompareTable({ cols }: { cols: CompareCol[] }) {
  const refCol = cols[0];
  const refNumber = refCol?.runNumber ?? null;
  const reasons = cols.filter((c) => c.unavailableReason !== null);
  return (
    <>
      <table className="frf-compare num" data-testid="frf-compare-table">
        <thead>
          <tr>
            <th className="frf-l">corner</th>
            <th className="frf-l">quantity</th>
            {cols.map((c, i) => (
              <th key={c.runId} data-testid={`frf-compare-col-${c.runNumber}`}>
                R{c.runNumber}
                {i === 0 ? <span className="frf-band-range">ref</span> : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {FRF_CORNERS.map((corner) =>
            COMPARE_QUANTITIES.map((q, qi) => {
              const refValue = cornerMid(refCol?.summary ?? null, corner, q.field);
              return (
                <tr
                  key={`${corner}-${q.key}`}
                  data-testid={`frf-compare-${corner}-${q.key}`}
                >
                  {qi === 0 ? (
                    <th scope="rowgroup" rowSpan={2} className="frf-l">
                      {FRF_CORNER_LABEL[corner]}
                    </th>
                  ) : null}
                  <td className="frf-l">{q.label}</td>
                  {cols.map((c, i) => {
                    const v = cornerMid(c.summary, corner, q.field);
                    const tone = i === 0 ? undefined : closenessTone(v, refValue);
                    return (
                      <td
                        key={c.runId}
                        className={tone}
                        data-testid={`frf-compare-${corner}-${q.key}-r${c.runNumber}`}
                      >
                        {fmt(v, q.digits)}
                      </td>
                    );
                  })}
                </tr>
              );
            }),
          )}
        </tbody>
      </table>
      <p className="muted frf-legend">
        Mid-band (4-12 Hz), magnitude bias in dB and phase error in degrees.
        Coloured versus R{refNumber}: green is closer to 0, amber is further.
      </p>
      {reasons.map((c) => (
        <p
          key={c.runId}
          className="muted frf-reason"
          data-testid={`frf-compare-reason-${c.runNumber}`}
        >
          R{c.runNumber}: {c.unavailableReason}
        </p>
      ))}
    </>
  );
}

// --- fetch + cache hook -----------------------------------------------------

// Fetch each run id at most once, abort in-flight fetches for ids that drop out
// of the set, and keep settled entries (artifacts are immutable).
function useFrfCache(runIds: number[]): Map<number, FetchState> {
  const runIdsKey = runIds.join(",");
  const [cache, setCache] = useState<Map<number, FetchState>>(new Map());
  const cacheRef = useRef(cache);
  cacheRef.current = cache;
  const controllersRef = useRef<Map<number, AbortController>>(new Map());

  useEffect(() => {
    const ids = runIdsKey === "" ? [] : runIdsKey.split(",").map(Number);

    // Abort + drop in-flight fetches for ids no longer needed.
    for (const [id, ctrl] of controllersRef.current.entries()) {
      if (!ids.includes(id)) {
        ctrl.abort();
        controllersRef.current.delete(id);
      }
    }

    for (const id of ids) {
      const existing = cacheRef.current.get(id);
      if (existing && existing.kind !== "loading") continue; // cached (immutable)
      if (controllersRef.current.has(id)) continue; // already in flight

      const ctrl = new AbortController();
      controllersRef.current.set(id, ctrl);
      setCache((prev) => {
        if (prev.get(id)?.kind === "ready") return prev;
        return new Map(prev).set(id, { kind: "loading" });
      });
      fetchFrfSummary(id, { signal: ctrl.signal }).then(
        (data) => {
          controllersRef.current.delete(id);
          if (ctrl.signal.aborted) return;
          setCache((prev) => new Map(prev).set(id, { kind: "ready", data }));
        },
        (err: unknown) => {
          controllersRef.current.delete(id);
          if (ctrl.signal.aborted) return;
          if ((err as { name?: string }).name === "AbortError") return;
          setCache((prev) =>
            new Map(prev).set(id, {
              kind: "error",
              message: err instanceof Error ? err.message : String(err),
            }),
          );
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runIdsKey]);

  // Abort everything on unmount.
  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      for (const ctrl of controllers.values()) ctrl.abort();
      controllers.clear();
    };
  }, []);

  return cache;
}

// --- panel ------------------------------------------------------------------

function runNumberOf(test: Test, runId: number): number | null {
  const run = (test.runs as Run[] | undefined)?.find((r) => r.id === runId);
  return run ? run.run_number : null;
}

export function FrfPanel({
  test,
  mode,
  singleRunId,
  compareRunIds,
}: FrfPanelProps) {
  const runIds =
    mode === "compare"
      ? compareRunIds
      : singleRunId != null
        ? [singleRunId]
        : [];
  const cache = useFrfCache(runIds);

  // Nothing to describe: no complete run (single) or empty selection (compare).
  if (runIds.length === 0) return null;

  const subtitle =
    mode === "compare"
      ? `${runIds.length} runs`
      : `R${runNumberOf(test, runIds[0]) ?? "?"}`;

  let body: ReactNode;
  if (mode === "compare") {
    const cols: CompareCol[] = compareRunIds.map((runId) => {
      const state = cache.get(runId);
      let summary: FrfArtifact | null = null;
      let unavailableReason: string | null = null;
      if (state?.kind === "ready") {
        if (state.data.available) summary = state.data;
        else unavailableReason = state.data.reason;
      } else if (state?.kind === "error") {
        unavailableReason = state.message;
      }
      return {
        runId,
        runNumber: runNumberOf(test, runId),
        summary,
        unavailableReason,
      };
    });
    body = <CompareTable cols={cols} />;
  } else {
    const state = cache.get(runIds[0]);
    if (!state || state.kind === "loading") {
      body = (
        <p className="muted frf-reason" data-testid="frf-loading">
          Loading FRF summary…
        </p>
      );
    } else if (state.kind === "error") {
      body = (
        <p className="muted frf-reason" data-testid="frf-error">
          Could not load FRF summary: {state.message}
        </p>
      );
    } else if (!state.data.available) {
      // Replay studies and runs without the artifact land here: one muted line
      // with the backend reason.
      body = (
        <p className="muted frf-reason" data-testid="frf-unavailable">
          No FRF summary: {state.data.reason}
        </p>
      );
    } else {
      const art = state.data;
      body = (
        <>
          <p className="muted frf-caption">
            Simulator versus recorded. Magnitude bias in dB, phase error in
            degrees; 0 is a perfect match. Coherence runs 0 to 1; a band below{" "}
            {COHERENCE_FLOOR.toFixed(1)} is de-emphasised as advisory.
          </p>
          <BandSummaryGrid art={art} />
          <PerCornerDetail art={art} />
          {art.advisory_note ? (
            <p className="frf-advisory muted" data-testid="frf-advisory">
              {art.advisory_note}
            </p>
          ) : null}
        </>
      );
    }
  }

  return (
    <section className="card frf-panel" data-testid="frf-panel">
      <div className="frf-head">
        FRF vs recorded (advisory)
        <span className="frf-head-sub" data-testid="frf-head-sub">
          {subtitle}
        </span>
      </div>
      {body}
    </section>
  );
}
